/**
 * DesignStudioScreen — Design Studio (Fase 2 · Bloco 1).
 *
 * Foco: ENTENDIMENTO VISUAL do escopo. Um único fluxo ("Gerar Design com IA")
 * usa a memória dos passos anteriores (ideação, contexto) e gera, em sequência:
 * (1) as TELAS do escopo (uma por módulo — cobertura completa), (2) protótipo
 * navegável e (3) Análise de Impacto Técnico. NÃO gera estrutura/arquitetura de
 * front-end: a tecnologia de front ainda não foi escolhida (isso é decidido depois).
 * O design é persistido na MEMÓRIA (mem_semantic) para os próximos fluxos.
 * Lê ?project=<id> (fallback: projeto mais recente).
 */
import { C } from '@/theme/palette';
import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProjects } from '@/api/projects';
import { useGenerateMissing, useApproveDesignScreens, useGenerateWebLayouts,
  useDesignStudio, useGenerateDesignStudio, useImpactAnalysis, useRunImpactAnalysis,
  useScopeProfile, useDesignProgress, useGenerateDesignBatch, useReorderDesign,
  useDesignPrototype, useGenerateDesignPrototype,
} from '@/api/designStudio';
import { AiTruncationNotice } from '@/components/ui';
import { DesignSystemCard } from '@/components/DesignSystemCard';
import { DesignProgressPanel, PrototipoCobertura, AvisosDaGeracao } from '@/components/DesignProgressPanel';
import { LayoutsCortados } from '@/components/LayoutsCortados';
import { ResumoDaAprovacao, SeloDeAprovacao } from '@/components/AprovacaoDasTelas';
import { ResumoDosLayoutsWeb, SeletorDePlataforma, layoutDaPlataforma } from '@/components/LayoutsWeb';
import { PainelDeNavegacao } from '@/components/PainelDeNavegacao';
import {
  useNavegacao, useDecidirTransicao, useDecidirEmLote, useConfirmarCasca, useProporDestinos,
} from '@/api/designNavegacao';

// Paleta: segue o tema global (aliases bg/surface/border/text/dim/muted… em theme/palette.js).

// Passos do fluxo único do Design (na ordem de execução).
// NÃO há "estrutura de arquivos front-end": a tecnologia de front ainda não foi
// escolhida — esta etapa é só ENTENDIMENTO VISUAL do escopo (telas/mockups).
const FLOW_STEPS = [
  ['telas', 'Telas do escopo'],
  ['prototipo', 'Protótipo navegável'],
  ['impacto', 'Análise de impacto'],
];
const PHASE_LABEL = {
  telas: 'Gerando TODAS as telas dos módulos para validação visual (negócio × produto)…',
  prototipo: 'Montando o protótipo navegável e enquadrando as telas com capricho…',
  impacto: 'Rodando a análise de impacto…',
};

export function DesignStudioScreen({ projectId: wizardProjectId } = {}) {
  const [searchParams] = useSearchParams();
  const urlProject = searchParams.get('project');
  const { data: projectsRaw } = useProjects();
  const projects = useMemo(
    () => (Array.isArray(projectsRaw) ? projectsRaw : projectsRaw?.items ?? []),
    [projectsRaw],
  );
  const projectId = wizardProjectId || urlProject || projects?.[0]?.id || null;
  const currentProject = useMemo(
    () => projects.find((p) => p.id === projectId) || null,
    [projects, projectId],
  );
  // Brownfield (repo existente): o Design Studio NÃO cria design novo — reflete a UI
  // indexada e aplica só os ajustes do escopo (default brownfield, igual ao backend).
  const isBrownfield =
    ((currentProject?.project_type ?? currentProject?.type) || 'brownfield') === 'brownfield';

  const { data, isLoading } = useDesignStudio(projectId);
  const generate = useGenerateDesignStudio(projectId);
  const { data: impact } = useImpactAnalysis(projectId);
  const runImpact = useRunImpactAnalysis(projectId);
  const { data: profile } = useScopeProfile(projectId);
  const integrationOnly = profile && profile.needs_ui === false;
  const { data: prototype } = useDesignPrototype(projectId);
  const genPrototype = useGenerateDesignPrototype(projectId);
  const prototypeHtml = genPrototype.data?.html ?? prototype?.html ?? null;

  // Renderiza o protótipo por BLOB URL (não srcDoc): dá ao iframe uma URL real,
  // então a navegação por âncora CSS :target funciona. Também alimenta
  // "abrir em nova aba" e "baixar" com a mesma URL.
  const [protoUrl, setProtoUrl] = useState(null);
  useEffect(() => {
    if (!prototypeHtml) { setProtoUrl(null); return undefined; }
    const url = URL.createObjectURL(new Blob([prototypeHtml], { type: 'text/html' }));
    setProtoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [prototypeHtml]);

  const screens = data?.screens ?? [];

  // Layout cortado (a IA parou no meio do HTML): a tela não vai ao codegen como referência.
  // "Consertar" refaz SÓ as cortadas, no mesmo lugar da lista.
  const consertarLayouts = useGenerateMissing(projectId);
  // Aprovação real das telas: com pelo menos uma aprovada, o codegen usa só as aprovadas.
  const aprovarTelas = useApproveDesignScreens(projectId);
  // Layout WEB próprio de cada tela: gerado em pacotes, até não faltar nenhum (ou a IA parar
  // de entregar). `plataforma` escolhe qual mockup a lista mostra.
  const gerarWeb = useGenerateWebLayouts(projectId);
  const [plataforma, setPlataforma] = useState('celular');
  // Navegação do produto como dado: o mapa é derivado das telas; aqui o dono decide o que a
  // regra não decidiu e confirma a barra principal de cada plataforma.
  const navegacao = useNavegacao(projectId);
  const decidirTransicao = useDecidirTransicao(projectId);
  const decidirEmLote = useDecidirEmLote(projectId);
  const confirmarCasca = useConfirmarCasca(projectId);
  const proporDestinos = useProporDestinos(projectId);
  const assinaturaDasTelas = screens.map((s) => s.id).join(',');
  const recarregarNavegacao = navegacao.refetch;
  useEffect(() => {
    // as telas mudaram (geradas, refeitas, consertadas): o mapa é derivado delas
    if (assinaturaDasTelas && recarregarNavegacao) recarregarNavegacao();
  }, [assinaturaDasTelas, recarregarNavegacao]);
  const erroDaNavegacao = [decidirTransicao, decidirEmLote, confirmarCasca, proporDestinos]
    .map((m) => m.error?.title || m.error?.message).find(Boolean) || null;
  const [gerandoWeb, setGerandoWeb] = useState(false);
  const [avisosWeb, setAvisosWeb] = useState([]);
  const [erroWeb, setErroWeb] = useState(null);
  const gerarLayoutsWeb = async () => {
    if (gerandoWeb) return;
    setGerandoWeb(true); setErroWeb(null); setAvisosWeb([]);
    try {
      // teto de pacotes só contra laço sem fim; o normal é parar em hasMore=false
      for (let i = 0; i < 200; i += 1) {
        const res = await gerarWeb.mutateAsync({ size: 5 });
        setAvisosWeb(Array.isArray(res?.avisos) ? res.avisos : []);
        if (!res?.hasMore) break;
      }
      setPlataforma('web');
    } catch (e) {
      setErroWeb(e?.title || e?.message || String(e));
    } finally {
      setGerandoWeb(false);
    }
  };

  // ── Fluxo único do Design (1 clique) ───────────────────────────────────────
  // Ordem: telas do escopo → protótipo navegável → impacto.
  // Considera a memória dos passos anteriores (a geração no backend puxa
  // ideação/contexto via fetch_project_scope) e persiste o design na memória
  // para os próximos fluxos. SEM estrutura de front-end (stack não escolhida).
  const [phase, setPhase] = useState(null);
  const [flowError, setFlowError] = useState(null);
  const busy = phase !== null;

  // Progresso POR FASE (planejando → DS → telas → cores → gravando → concluído), com X/N,
  // ritmo e estimativa. Polling enquanto as telas são geradas (fluxo único ou pacotes).
  // `progDesde` descarta o estado da geração anterior que ainda esteja no servidor.
  const [batchBusy, setBatchBusy] = useState(false);
  const [progDesde, setProgDesde] = useState(0);
  const { data: progress } = useDesignProgress(projectId, phase === 'telas' || batchBusy);

  const runDesignFlow = async () => {
    if (busy) return;
    setFlowError(null);
    setProgDesde(Date.now());
    try {
      // SEMPRE (re)gera as telas — "Regerar" deve refazer a cobertura completa dos
      // módulos, não reaproveitar as telas antigas. O backend substitui a versão 1.
      setPhase('telas'); await generate.mutateAsync();
      try { await reorder.mutateAsync(); } catch (_e) { /* ordem é best-effort */ }
      setPhase('prototipo'); await genPrototype.mutateAsync();
      setPhase('impacto');   await runImpact.mutateAsync();
    } catch (e) {
      setFlowError(e?.title || e?.message || String(e));
    } finally {
      setPhase(null);
    }
  };

  // ── DS-v2: geração INCREMENTAL por PACOTES de 10 (com confirmação) ──────────
  const genBatch = useGenerateDesignBatch(projectId);
  const reorder = useReorderDesign(projectId);
  const [batchMode, setBatchMode] = useState(false);   // fluxo por pacotes ativo?
  const [batchHasMore, setBatchHasMore] = useState(true);
  const [batchPkgs, setBatchPkgs] = useState(0);       // pacotes já gerados
  const [batchPlanned, setBatchPlanned] = useState(0); // telas que o escopo exige (N)
  const [batchLastNew, setBatchLastNew] = useState(0); // telas novas no último pacote
  const [deadNote, setDeadNote] = useState('');        // botão morto reportado (#4)
  const [batchAvisos, setBatchAvisos] = useState([]);  // tetos/falhas do último pacote
  const [batchErr, setBatchErr] = useState(null);

  // Gera 1 pacote. reset=true zera a versão (só no 1º). note = correção de botão morto.
  const runBatch = async ({ reset = false } = {}) => {
    if (batchBusy) return;
    setBatchBusy(true); setBatchErr(null); setProgDesde(Date.now());
    try {
      const res = await genBatch.mutateAsync({ size: 10, reset, note: (deadNote || '').trim() });
      setBatchMode(true);
      setBatchHasMore(Boolean(res?.hasMore));
      setBatchPkgs((n) => n + 1);
      setBatchPlanned(Number(res?.plannedTotal) || 0);
      setBatchLastNew((res?.newScreens?.length) ?? 0);
      setBatchAvisos(Array.isArray(res?.avisos) ? res.avisos : []);
      setDeadNote(''); // consumida — vira nota de escopo e entra no próximo pacote
      // Mantém SEMPRE a ordem da jornada do produto (automático, sem clicar no botão).
      try { await reorder.mutateAsync(); } catch (_e) { /* best-effort */ }
    } catch (e) {
      setBatchErr(e?.title || e?.message || String(e));
    } finally {
      setBatchBusy(false);
    }
  };
  // "Gerar todo o restante de uma vez" → cai no fluxo em lote (backend converge).
  const finishAllRemaining = async () => {
    if (batchBusy) return;
    setBatchBusy(true); setBatchErr(null); setProgDesde(Date.now());
    try {
      await generate.mutateAsync();            // gera tudo até convergir (substitui v1)
      try { await reorder.mutateAsync(); } catch (_e) { /* ordem é best-effort */ }
      await genPrototype.mutateAsync();
      setBatchHasMore(false);
    } catch (e) {
      setBatchErr(e?.title || e?.message || String(e));
    } finally {
      setBatchBusy(false);
    }
  };
  const stopBatch = () => { setBatchMode(false); setBatchHasMore(true); setBatchPkgs(0); };

  if (!projectId) {
    return <div style={{ padding: 40, color: C.dim }}>Nenhum projeto selecionado.</div>;
  }

  const impactItems = impact?.items ?? [];
  const hasDesign = Boolean(prototypeHtml) || screens.length > 0;

  return (
    <div className="sp-page" style={{ color: C.text }}>
      {integrationOnly && (
        <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 10,
          background: 'rgba(123,59,255,0.10)', border: `1px solid ${C.quantum}55`, color: C.text, fontSize: 13 }}>
          <strong style={{ color: C.quantum }}>Escopo de integração — telas opcionais.</strong>{' '}
          Pela análise do escopo, este projeto {profile?.reason
            ? `não requer telas de UI (${profile.reason}).`
            : 'parece ser só de integração/backend, sem telas de usuário final.'}{' '}
          Você pode <strong>pular o Design Studio</strong> — ou gerar o protótipo mesmo assim.
        </div>
      )}

      {/* Design System (Onda 1): escolhido ANTES de gerar as telas */}
      <DesignSystemCard projectId={projectId} brownfield={isBrownfield} />

      {/* Header + ação única */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 22, letterSpacing: '-0.02em' }}>Design Studio</h2>
        {data?.version > 0 && (
          <span style={{ fontFamily: 'monospace', fontSize: 11, padding: '3px 9px', borderRadius: 6,
            background: data.version === 2 ? 'rgba(253,0,213,0.12)' : 'rgba(1,230,228,0.12)',
            color: data.version === 2 ? C.digital : C.accent }}>
            v{data.version} · {data.version === 2 ? 'rascunho' : (isBrownfield ? 'ajustes' : 'proposta')}
          </span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!batchMode && (
            <button
              onClick={() => runBatch({ reset: true })}
              disabled={busy || batchBusy}
              title="Gera as telas em pacotes de 10, pausando para você validar a navegação a cada pacote"
              style={{ cursor: (busy || batchBusy) ? 'default' : 'pointer',
                background: 'transparent', color: C.quantum, border: `1px solid ${C.quantum}88`,
                borderRadius: 8, padding: '9px 14px', fontSize: 13, fontWeight: 700,
                opacity: (busy || batchBusy) ? 0.6 : 1 }}>
              {batchBusy ? 'Gerando pacote…' : '📦 Gerar por pacotes de 10'}
            </button>
          )}
          <button
            onClick={runDesignFlow}
            disabled={busy || batchBusy}
            style={{ cursor: (busy || batchBusy) ? 'default' : 'pointer',
              background: C.accent, color: C.black, border: 'none', borderRadius: 8,
              padding: '9px 16px', fontSize: 13, fontWeight: 700, opacity: (busy || batchBusy) ? 0.7 : 1 }}>
            {busy ? (PHASE_LABEL[phase] || 'Gerando…') : (hasDesign ? '↻ Regerar tudo com IA' : '✨ Gerar tudo com IA')}
          </button>
        </div>
      </div>

      {/* ── DS-v2: painel de geração por PACOTES (confirmação + botão morto) ── */}
      {batchMode && (
        <div style={{ marginTop: 16, border: `1px solid ${C.quantum}66`, borderRadius: 12,
          background: 'rgba(123,59,255,0.08)', padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <strong style={{ color: C.quantum, fontSize: 14 }}>📦 Geração por pacotes</strong>
            <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: C.accent,
              padding: '3px 10px', borderRadius: 6, background: 'rgba(1,230,228,0.12)' }}>
              {screens.length}{batchPlanned > 0 ? ` / ${Math.max(batchPlanned, screens.length)}` : ''} telas
            </span>
            <span style={{ fontSize: 12.5, color: C.dim }}>
              {batchPkgs} pacote(s){batchLastNew > 0 ? ` · +${batchLastNew} no último` : ''}
              {batchBusy ? ' · gerando…' : batchHasMore
                ? (batchPlanned > 0 ? ` · faltam ~${Math.max(0, batchPlanned - screens.length)}` : ' · ainda há módulos')
                : ' · cobertura completa ✓'}
            </span>
          </div>
          {batchPlanned > 0 && (
            <div style={{ marginTop: 8, height: 6, borderRadius: 999, background: C.border, overflow: 'hidden' }}>
              <div style={{ height: '100%',
                width: `${Math.min(100, Math.round((screens.length / Math.max(batchPlanned, screens.length)) * 100))}%`,
                background: batchHasMore ? C.quantum : C.accent, transition: 'width 400ms ease' }} />
            </div>
          )}
          <p style={{ color: C.muted, fontSize: 12.5, margin: '10px 0 8px', lineHeight: 1.5 }}>
            Valide a navegação das telas geradas (abaixo). <strong style={{ color: C.text }}>Algum botão
            não funciona ou falta uma tela?</strong> Descreva aqui — a correção entra no próximo pacote.
          </p>
          <textarea
            value={deadNote}
            onChange={(e) => setDeadNote(e.target.value)}
            disabled={batchBusy}
            placeholder="Ex.: na tela 'Agenda', o botão 'Novo agendamento' não abre nada; falta a tela de confirmação."
            rows={2}
            style={{ width: '100%', boxSizing: 'border-box', background: C.black, color: C.text,
              border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 12px', fontSize: 13,
              resize: 'vertical', fontFamily: 'inherit' }}
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
            <button
              onClick={() => runBatch({ reset: false })}
              disabled={batchBusy || !batchHasMore}
              style={{ cursor: (batchBusy || !batchHasMore) ? 'default' : 'pointer',
                background: C.quantum, color: '#fff', border: 'none', borderRadius: 8,
                padding: '9px 15px', fontSize: 13, fontWeight: 700, opacity: (batchBusy || !batchHasMore) ? 0.5 : 1 }}>
              {batchBusy ? 'Gerando…' : (deadNote.trim() ? '↻ Corrigir + próximo pacote' : '▶ Continuar (próx. 10)')}
            </button>
            <button
              onClick={finishAllRemaining}
              disabled={batchBusy || !batchHasMore}
              style={{ cursor: (batchBusy || !batchHasMore) ? 'default' : 'pointer',
                background: 'transparent', color: C.accent, border: `1px solid ${C.accent}88`,
                borderRadius: 8, padding: '9px 15px', fontSize: 13, fontWeight: 700,
                opacity: (batchBusy || !batchHasMore) ? 0.5 : 1 }}>
              ⏩ Gerar todo o restante de uma vez
            </button>
            <button
              onClick={stopBatch}
              disabled={batchBusy}
              style={{ cursor: batchBusy ? 'default' : 'pointer', background: 'transparent',
                color: C.dim, border: `1px solid ${C.border}`, borderRadius: 8,
                padding: '9px 15px', fontSize: 13, fontWeight: 600, opacity: batchBusy ? 0.5 : 1 }}>
              ✓ Parar aqui
            </button>
          </div>
          {batchBusy && (
            <div style={{ marginTop: 10 }}>
              <DesignProgressPanel progress={progress} desde={progDesde} />
            </div>
          )}
          {!batchBusy && <AvisosDaGeracao avisos={batchAvisos} />}
          {batchErr && (
            <div style={{ marginTop: 10, color: C.digital, fontSize: 12.5 }}>Falha no pacote: {String(batchErr)}</div>
          )}
        </div>
      )}

      <p style={{ color: C.dim, maxWidth: '78ch', marginTop: 10, fontSize: 13.5 }}>
        O squad de Produto/UX gera, a partir de <strong style={{ color: C.text }}>tudo já capturado nos passos
        anteriores</strong> (ideação e contexto), as <strong style={{ color: C.accent }}>telas do escopo</strong> e um{' '}
        <strong style={{ color: C.quantum }}>protótipo navegável</strong> — o objetivo é{' '}
        <strong style={{ color: C.text }}>entender 100% do escopo de forma visual</strong>. Aqui{' '}
        <strong style={{ color: C.text }}>não</strong> se define tecnologia nem estrutura de front-end (a stack é
        escolhida depois). Fica salvo na <strong style={{ color: C.text }}>memória do projeto</strong> e a{' '}
        <strong style={{ color: C.digital }}>análise de impacto</strong> roda no mesmo fluxo.
      </p>

      {isBrownfield && (
        <div style={{ marginTop: 12, padding: '11px 15px', borderRadius: 10,
          background: 'rgba(1,230,228,0.08)', border: `1px solid ${C.accent}44`,
          color: C.text, fontSize: 13, lineHeight: 1.5 }}>
          <strong style={{ color: C.accent }}>Projeto existente (brownfield).</strong>{' '}
          O Design Studio <strong>não propõe um design novo</strong> — não há como conhecer o
          design original. Ele reflete a interface que já está no{' '}
          <strong style={{ color: C.text }}>código-fonte indexado</strong> e aplica{' '}
          <strong>somente os ajustes</strong> que o escopo pede, preservando o layout atual.
        </div>
      )}

      {/* Progresso do fluxo */}
      {busy && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
          {FLOW_STEPS.map(([k, label]) => {
            const active = phase === k;
            const done = FLOW_STEPS.findIndex((s) => s[0] === phase) > FLOW_STEPS.findIndex((s) => s[0] === k);
            return (
              <span key={k} style={{ fontSize: 12, padding: '5px 11px', borderRadius: 999,
                border: `1px solid ${active ? C.accent : done ? `${C.accent}55` : C.border}`,
                color: active ? C.black : done ? C.accent : C.muted,
                background: active ? C.accent : 'transparent', fontWeight: active ? 700 : 500 }}>
                {done ? '✓ ' : active ? '● ' : '○ '}{label}
              </span>
            );
          })}
        </div>
      )}

      {busy && (
        <div style={{ marginTop: 10, fontSize: 12.5, color: C.dim, lineHeight: 1.5 }}>
          {PHASE_LABEL[phase] || 'Gerando…'}
          {/* Fase, X/N, ritmo/estimativa e a proposta de DS; "concluído" só na fase final */}
          {phase === 'telas' && (
            <div style={{ marginTop: 8 }}>
              <DesignProgressPanel progress={progress} desde={progDesde} />
            </div>
          )}
          <div style={{ color: C.muted, marginTop: 6 }}>
            — priorizamos a <strong style={{ color: C.text }}>qualidade visual</strong>: geramos uma tela
            BONITA e enquadrada para CADA módulo do escopo, para você validar o negócio × produto.
            Pode levar um tempo — o que importa é o acabamento, não a velocidade.
          </div>
        </div>
      )}

      {!busy && <AvisosDaGeracao avisos={generate.data?.avisos} />}
      {!busy && !batchBusy && (
        <LayoutsCortados
          screens={screens}
          consertando={consertarLayouts.isPending}
          erro={consertarLayouts.error?.title || consertarLayouts.error?.message || null}
          onConsertar={() => consertarLayouts.mutate({ repair_layouts: true })}
        />
      )}
      {!busy && !batchBusy && <AvisosDaGeracao avisos={consertarLayouts.data?.avisos} />}

      {flowError && (
        <div style={{ marginTop: 12, color: C.digital, fontSize: 13 }}>
          Falha no fluxo do Design: {String(flowError)}
        </div>
      )}

      {/* ── HERÓI: Protótipo navegável (arquivo único, telas clicáveis) ── */}
      {(prototypeHtml || phase === 'prototipo') && (
        <div style={{ marginTop: 18, border: `1px solid ${C.quantum}55`, borderRadius: 12, overflow: 'hidden',
          background: C.surface }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
            borderBottom: `1px solid ${C.border}`, background: 'rgba(123,59,255,0.10)' }}>
            <strong style={{ fontSize: 14, color: C.text }}>🧭 Protótipo navegável</strong>
            <span style={{ fontSize: 12, color: C.dim }}>gerado pela IA · telas clicáveis</span>
            {prototypeHtml && (
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <a href={protoUrl || '#'} target="_blank" rel="noreferrer"
                  style={{ textDecoration: 'none', cursor: protoUrl ? 'pointer' : 'default',
                    color: C.accent, border: `1px solid ${C.accent}66`, borderRadius: 7,
                    padding: '5px 11px', fontSize: 12, opacity: protoUrl ? 1 : 0.5 }}>
                  ↗ Abrir em nova aba
                </a>
                <a href={protoUrl || '#'} download="prototipo.html"
                  style={{ textDecoration: 'none', cursor: protoUrl ? 'pointer' : 'default',
                    color: C.dim, border: `1px solid ${C.border}`, borderRadius: 7,
                    padding: '5px 11px', fontSize: 12, opacity: protoUrl ? 1 : 0.5 }}>
                  ⬇ Baixar HTML
                </a>
              </div>
            )}
          </div>
          {phase === 'prototipo' && !prototypeHtml && (
            <div style={{ padding: 24, color: C.dim, fontSize: 13 }}>Gerando o protótipo navegável com IA…</div>
          )}
          {prototypeHtml && (
            <div style={{ padding: '0 14px' }}>
              <AiTruncationNotice
                complete={genPrototype.data?.aiComplete ?? prototype?.aiComplete}
                onRetry={runDesignFlow}
              />
              <PrototipoCobertura prototipo={genPrototype.data ?? prototype} />
            </div>
          )}
          {prototypeHtml && protoUrl && (
            <iframe
              title="Protótipo navegável"
              src={protoUrl}
              /* allow-same-origin p/ carregar o blob:; SEM allow-scripts (protótipo é
                 CSS :target puro); allow-popups p/ links target=_blank. */
              sandbox="allow-same-origin allow-popups"
              style={{ width: '100%', height: 680, border: 0, background: C.black, display: 'block' }}
            />
          )}
        </div>
      )}

      {/* ── Análise de Impacto Técnico (roda no fluxo, sem botão à parte) ── */}
      {(impactItems.length > 0 || phase === 'impacto') && (
        <div style={{ marginTop: 16, border: `1px solid ${C.border}`, background: C.surface,
          borderRadius: 12, padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
            <h4 style={{ margin: 0, fontFamily: 'monospace', fontSize: 11, textTransform: 'uppercase',
              letterSpacing: '0.1em', color: C.digital }}>Análise de Impacto Técnico</h4>
            {impact?.estTokens > 0 && (
              <span style={{ fontFamily: 'monospace', fontSize: 11, color: C.dim }}>
                +{impact.estTokens.toLocaleString('pt-BR')} tk · US$ {Number(impact.estCost || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            )}
            <span style={{ fontSize: 11, color: C.muted, marginLeft: 'auto' }}>
              {phase === 'impacto' && impactItems.length === 0 ? 'analisando…' : 'no fluxo'}
            </span>
          </div>
          {impactItems.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {impactItems.map((it, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13,
                  border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 12px', background: C.surface2 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 10, padding: '2px 7px', borderRadius: 5,
                    background: C.black, color: C.digital }}>{it.area}</span>
                  <span style={{ flex: 1, color: C.dim }}>{it.change}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 10, color: C.muted }}>{it.effort}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {isLoading && <div style={{ marginTop: 20, color: C.dim }}>Carregando…</div>}

      {!isLoading && !hasDesign && !busy && (
        <div style={{ marginTop: 24, padding: 28, border: `1px dashed ${C.border}`, borderRadius: 12,
          color: C.dim, textAlign: 'center' }}>
          Clique em <strong style={{ color: C.text }}>Gerar Design com IA</strong> — o squad cria as telas do
          escopo (uma por módulo) e o protótipo navegável a partir do escopo capturado, e roda a análise de impacto.
        </div>
      )}

      {/* ── Telas geradas — MOCKUP HTML de cada tela (visual, cresce a cada pacote) ── */}
      {screens.length > 0 && (
        <div style={{ marginTop: 26 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
            <h4 style={{ margin: 0, fontFamily: 'monospace', fontSize: 11, textTransform: 'uppercase',
              letterSpacing: '0.1em', color: C.accent }}>Telas geradas — mockups</h4>
            <span style={{ fontSize: 12, color: C.dim }}>{screens.length} tela(s) · valide o visual e a navegação</span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                onClick={() => reorder.mutate()}
                disabled={reorder.isPending || busy || batchBusy}
                title="Reordena as telas na sequência da jornada do produto (onboarding → login → home → módulos → config), sem regerar"
                style={{ cursor: (reorder.isPending || busy || batchBusy) ? 'default' : 'pointer',
                  background: 'transparent', color: C.accent, border: `1px solid ${C.accent}66`,
                  borderRadius: 7, padding: '5px 11px', fontSize: 12, fontWeight: 600,
                  opacity: (reorder.isPending || busy || batchBusy) ? 0.6 : 1 }}>
                {reorder.isPending ? 'Ordenando…' : '↕ Ordenar pela jornada do produto'}
              </button>
            </div>
          </div>
          <AiTruncationNotice complete={data?.aiComplete} onRetry={runDesignFlow} />
          <PainelDeNavegacao
            mapa={navegacao.data}
            sugestoes={proporDestinos.data?.propostas || []}
            avisos={proporDestinos.data?.avisos || []}
            erro={erroDaNavegacao}
            ocupado={decidirTransicao.isPending || decidirEmLote.isPending || confirmarCasca.isPending}
            sugerindo={proporDestinos.isPending}
            onDecidir={(d) => decidirTransicao.mutate(d)}
            onConfirmarSugestoes={(lista) => decidirEmLote.mutate(lista)}
            onPedirSugestoes={() => proporDestinos.mutate()}
            onConfirmarCasca={(c) => confirmarCasca.mutate(c)}
          />
          <ResumoDaAprovacao
            screens={screens}
            ocupado={aprovarTelas.isPending || busy || batchBusy}
            recusadas={aprovarTelas.data?.refused || []}
            erro={aprovarTelas.error?.title || aprovarTelas.error?.message || null}
            onAprovarTodas={() => aprovarTelas.mutate({ all: true })}
          />
          <ResumoDosLayoutsWeb
            screens={screens}
            gerando={gerandoWeb}
            avisos={avisosWeb}
            erro={erroWeb}
            onGerar={gerarLayoutsWeb}
          />
          <div style={{ margin: '0 0 12px' }}>
            <SeletorDePlataforma valor={plataforma} onMudar={setPlataforma} />
          </div>
          <div style={{ display: 'grid', gap: 16, gridTemplateColumns: plataforma === 'web'
            ? '1fr' : 'repeat(auto-fill, minmax(360px, 1fr))' }}>
            {screens.map((s, idx) => (
              <div key={s.id} style={{ border: `1px solid ${C.border}`, background: C.surface,
                borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
                  borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ width: 22, height: 22, borderRadius: 6, background: 'rgba(1,230,228,0.12)',
                    color: C.accent, display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 700 }}>
                    {idx + 1}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 13, flex: 1 }}>{s.name}</div>
                  <SeloDeAprovacao
                    screen={s}
                    ocupado={aprovarTelas.isPending || busy || batchBusy}
                    onMudar={(aprovar) => aprovarTelas.mutate({ screen_ids: [s.id], approve: aprovar })}
                  />
                  {layoutDaPlataforma(s, plataforma) && (
                    <a href={`data:text/html;charset=utf-8,${encodeURIComponent(layoutDaPlataforma(s, plataforma))}`}
                      target="_blank" rel="noreferrer"
                      title="Abrir a tela em tamanho real (nova aba)"
                      style={{ textDecoration: 'none', color: C.dim, border: `1px solid ${C.border}`,
                        borderRadius: 6, padding: '3px 9px', fontSize: 11 }}>↗ ampliar</a>
                  )}
                </div>
                {/* MOCKUP em RETRATO (celular) centralizado — iframe LEVE com lazy-load
                    (renderizar 100+ telas via Shadow DOM de uma vez travava a página). */}
                {layoutDaPlataforma(s, plataforma) ? (
                  <div style={{ background: C.black, display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
                    <iframe
                      key={plataforma}
                      title={`mockup-${plataforma}-${s.name}`}
                      srcDoc={layoutDaPlataforma(s, plataforma)}
                      loading="lazy"
                      /* allow-same-origin (sem allow-scripts) → o pai lê a altura real do
                         conteúdo p/ ajustar o iframe; sem script, seguro. */
                      sandbox="allow-same-origin"
                      onLoad={(e) => {
                        try {
                          const doc = e.target.contentDocument;
                          const h = doc && doc.body ? doc.body.scrollHeight : 0;
                          if (h > 80) e.target.style.height = `${Math.min(1600, h + 6)}px`;
                        } catch (_err) { /* mantém altura padrão */ }
                      }}
                      style={{ width: '100%', maxWidth: plataforma === 'web' ? 1280 : 468,
                        height: 744, border: 0,
                        background: C.black, display: 'block', margin: '0 auto' }}
                    />
                  </div>
                ) : (
                  <div style={{ padding: 16, color: C.muted, fontSize: 12 }}>
                    {plataforma === 'web'
                      ? '(esta tela ainda não tem layout web — use "Gerar layouts web")'
                      : '(sem mockup HTML — só metadados)'}
                  </div>
                )}
                {s.description && (
                  <p style={{ color: C.dim, fontSize: 12, margin: 0, padding: '9px 12px',
                    borderTop: `1px solid ${C.border}` }}>{s.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
